import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { LANDING } from "@/constants/testIds";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/8ac86e8d-d508-42f4-a992-5d8485e6789c/images/d25057a3a27bb33fc2a7ed5bba3d0c906dcc7ef78376acca4fe850193c7a031a.jpeg";

const LINES = ["Your city is a", "live playlist."];

const lineVariants = {
  hidden: {},
  visible: {},
};

const wordVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const KineticLine = ({ text, lineIndex }) => (
  <span className="block overflow-hidden">
    {text.split(" ").map((word, i) => (
      <span key={i} className="inline-block overflow-hidden mr-[0.28em]">
        <motion.span
          className="inline-block"
          custom={lineIndex * 2 + i}
          variants={wordVariants}
          initial="hidden"
          animate="visible"
        >
          {word}
        </motion.span>
      </span>
    ))}
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 120,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), {
    stiffness: 120,
    damping: 20,
  });

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100vh] w-full overflow-hidden flex items-center pt-28 pb-16"
      style={{
        background:
          "radial-gradient(1200px 800px at 20% 0%, #1a0b3a 0%, #07030f 55%, #030109 100%)",
      }}
    >
      {/* ambient glow blobs */}
      <motion.div
        className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-[#a259ff] opacity-20 blur-[120px]"
        animate={{ y: [0, 40, 0], x: [0, 20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 -right-32 h-[480px] w-[480px] rounded-full bg-[#22d3ee] opacity-[0.14] blur-[130px]"
        animate={{ y: [0, -30, 0], x: [0, -20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* grain overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#a259ff]/30 bg-[#a259ff]/10 px-4 py-1.5 text-xs font-medium text-[#e6d1ff] mb-8"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22d3ee] opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#22d3ee]" />
            </span>
            Real-time social music map
          </motion.div>

          <h1
            data-testid={LANDING.heroTitle}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-white"
          >
            <KineticLine text={LINES[0]} lineIndex={0} />
            <KineticLine text={LINES[1]} lineIndex={1} />
            <span className="block overflow-hidden mt-1">
              <motion.span
                custom={10}
                variants={wordVariants}
                initial="hidden"
                animate="visible"
                className="inline-block bg-gradient-to-r from-[#a259ff] via-[#c060ff] to-[#22d3ee] bg-clip-text text-transparent"
              >
                Powered by strangers.
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid={LANDING.heroSubtitle}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-md text-base sm:text-lg text-[#b9a9e6] leading-relaxed"
          >
            GeoBeats drops a live pin for everyone playing music around you.
            Tap in, listen along, or Shazam the air with Song Radar — all in
            real time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid={LANDING.heroCtaPrimary}
              onClick={() =>
                document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full bg-gradient-to-r from-[#a259ff] to-[#7c3aff] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(162,89,255,0.45)] hover:shadow-[0_0_45px_rgba(162,89,255,0.7)] hover:-translate-y-0.5 transition-all duration-300"
            >
              Drop your pin
            </button>
            <button
              data-testid={LANDING.heroCtaSecondary}
              onClick={() =>
                document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white/90 hover:border-[#22d3ee]/60 hover:text-[#22d3ee] transition-all duration-300"
            >
              See how it works
            </button>
          </motion.div>
        </div>

        <motion.div
          ref={ref}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          style={{ perspective: 1200 }}
          className="relative mx-auto lg:mx-0 w-full max-w-sm"
        >
          <motion.img
            data-testid={LANDING.heroPhoneMockup}
            src={HERO_IMAGE}
            alt="GeoBeats live music map on a phone"
            style={{ rotateX, rotateY }}
            animate={{ y: [0, -14, 0] }}
            transition={{ y: { duration: 5, repeat: Infinity, ease: "easeInOut" } }}
            className="w-full drop-shadow-[0_30px_60px_rgba(162,89,255,0.35)]"
            draggable={false}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#b9a9e6]/60"
      >
        <span className="text-[10px] uppercase tracking-[0.2em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-[1px] bg-gradient-to-b from-[#a259ff] to-transparent"
        />
      </motion.div>
    </section>
  );
};
